/**
 * The one path from a rasterized ticket-card blob to the OS share sheet (or
 * a download fallback where file-share is unsupported) — order-access.view.ts
 * (the buyer's own order page) and order-detail.view.ts (the admin console's
 * "as the buyer sees it" mirror of the same card) both call this, so the
 * filename convention and the share-vs-download decision can't drift between
 * the two surfaces. Callers own their own localized share text and toast
 * copy; this returns which path it took and nothing else.
 */

function slug(s: string): string {
	return s
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
}

/** `<event>-<ticket-type>.png`, filesystem-safe. */
export function ticketShareFileName(
	eventName: string,
	typeName: string,
): string {
	return `${slug(eventName)}-${slug(typeName)}.png`;
}

export type TicketShareResult = "shared" | "downloaded" | "cancelled";

/** The anchor + object-URL technique for handing a rasterized ticket a plain
 *  file download, no OS share sheet involved — `shareTicketCardImage`'s own
 *  unsupported-file-share fallback, and order-access's own explicit
 *  "download all my tickets" action, both resolve to this one path. */
export function downloadTicketCardImage(blob: Blob, fileName: string): void {
	const objectUrl = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = objectUrl;
	a.download = fileName;
	a.click();
	URL.revokeObjectURL(objectUrl);
}

/** Hands `blob` to `navigator.share()` as `fileName`, falling back to a
 *  download anchor where file-share is unsupported. An aborted native share
 *  (the user dismissing the OS sheet) resolves "cancelled" rather than
 *  throwing. */
export async function shareTicketCardImage(
	blob: Blob,
	fileName: string,
	shareText: string,
): Promise<TicketShareResult> {
	const file = new File([blob], fileName, { type: "image/png" });
	const canNativeShare =
		typeof navigator.share === "function" &&
		typeof navigator.canShare === "function" &&
		navigator.canShare({ files: [file] });

	if (canNativeShare) {
		try {
			await navigator.share({ files: [file], text: shareText });
			return "shared";
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError")
				return "cancelled";
			throw err;
		}
	}

	downloadTicketCardImage(blob, fileName);
	return "downloaded";
}
